import { rd } from '@passionware/monads';
import { injectConfig } from '@passionware/platform-react';
import { createMatcher } from '@passionware/query-toolkit';
import type { ReactElement } from 'react';
import type {
  ReportOrganization,
  ReportScope
} from 'v5/core/reporting/reportScope';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { ItemSelector } from 'v5/patterns/widgets/ItemSelector';
import { WithServices } from 'v5/platform/ts/services';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

const ORGANIZATION_OPTION_ID = 'organization';

const ReportScopeSelectorInner = injectConfig(ItemSelector<ReportScope>)
  .fromProps<
    WithServices<[WithEnterpriseService]> & {
      organizationId: string;
      organization: ReportOrganization;
      organizationLabel: string;
    }
  >(api => ({
    useOptions: (query, isDropdownOpen) => {
      const { organizationId, organization, organizationLabel, services } =
        api.useProps();
      const workspacesRd = services.enterpriseService.useWorkspaces(
        isDropdownOpen ? organizationId : null,
        'active'
      );
      return rd.map(workspacesRd, workspaces => [
        ...workspaces
          .filter(createMatcher(query, workspace => workspace.name))
          .map(workspace => ({
            id: workspace.id.toString(),
            label: workspace.name,
            content: (
              <div className="flex items-center gap-2">
                <EnterpriseWorkspaceIcon
                  name={workspace.name}
                  iconUrl={workspace.iconUrl}
                />
                <span>{workspace.name}</span>
              </div>
            ),
            extra: { type: 'workspace' as const, workspace }
          })),
        ...[
          {
            id: ORGANIZATION_OPTION_ID,
            label: organizationLabel,
            content: (
              <div className="flex items-center gap-2">
                <EnterpriseWorkspaceIcon
                  name={organization.name}
                  iconUrl={organization.iconUrl}
                />
                <span>{organizationLabel}</span>
              </div>
            ),
            extra: { type: 'organization' as const }
          }
        ].filter(createMatcher(query, option => option.label))
      ]);
    }
  }))
  .transformProps(x => x.passAll);

export type ReportScopeSelectorProps = WithServices<[WithEnterpriseService]> & {
  organizationId: string;
  organization: ReportOrganization;
  organizationLabel: string;
  onScopeSelect: (scope: ReportScope) => void;
  children: ReactElement;
};

/** Picks whose records a report holds: an active workspace, or the whole organisation. */
export function ReportScopeSelector({
  organizationId,
  organization,
  organizationLabel,
  onScopeSelect,
  children,
  services
}: ReportScopeSelectorProps) {
  return (
    <ReportScopeSelectorInner
      services={services}
      organizationId={organizationId}
      organization={organization}
      organizationLabel={organizationLabel}
      onSelect={(_id, _wasChecked, scope) => onScopeSelect(scope)}
    >
      {children}
    </ReportScopeSelectorInner>
  );
}
